/* eslint-disable no-undef */
import API from 'constants/api';
import { getParentUri, getViewMode } from 'helpers/fieldHelpers';
import { addValue, removeValue, setValue } from './fieldActions';
import { openModal, closeModal, addResourceToModal } from './modalActions';
import { getFormData, resToJsonHandleErrors, actionError } from 'helpers/actionHelpers';

import { SET_TEMPLATE } from 'constants/actionTypes';

/*
  Send request for Resource controller to remove resource from parent
  Dispatch field reducer to remove value from state after successful deletion
*/
const FORMAT_DELIMETER = ',';

export const removeResource = ({ predicate, index, value }) => (dispatch, getState) => {
  const formData = new FormData();
  const parentUri = getParentUri(getState());
  const state = getState();
  const archivedObjectUri = state.object.uri;
  formData.set('archivedObjectUri', archivedObjectUri);
  
  formData.set('objectUri', value.uri);

  formData.set('predicateUri', predicate);
  if (parentUri) {
    formData.set('parentUri', parentUri);
  }
  formData.set('delete', 'true');
  if (value.isArchivedObject) {
    return removeObjectLink({value, predicate, index})(dispatch, getState);
  }

  return fetch(API.DAM.EDIT_RESOURCE, {
    method: 'POST',
    body: formData
  })
    .then(resToJsonHandleErrors)
    .then(() => dispatch(removeValue(predicate, index)));
};

/**
 * Open modal for editing an existing resource for object
 * @param {*} uri
 * @param {*} predicate 
 * @param {*} index parent -> fields[predicate][index] 
 * @param {*} onSave execute after save & close
 */
export const editResource = (settings) => (dispatch, getState) => {
  const state = getState();
  // Mode is used to filter fields by modeConstraint interface.rdfs
  const mode = getViewMode(state);
  const parentUri = getParentUri(state);
  const archivedObjectUri = state.object.uri;
  const view =  state.object.view ? state.object.view : state.view.view.props.view;
  const { type, predicate, index, value } = settings;
  const resourceFormatChain = getResourceFormatChain(state.modal.resources, predicate);

  // ON SAVE & CLOSE
  if (!settings.onSave) {
    settings.onSave = resource => saveResource(resource, archivedObjectUri, view, resourceFormatChain)
      .then(({ value }) => dispatch(setValue(predicate, index, value)));
  }

  // 1. OPEN MODAL IF NOT OPEN
  if (!state.modal.showing) {
    dispatch(openModal(settings));
  }

  // 2. URL FOR FETCHING RESOURCE JSON
  const params = new URLSearchParams({
    predicate,
    parentUri,
    uri: value.uri,
    mode,
    view,
    archivedObjectUri,
    resourceFormatChain
  });
  if (type) { // rdf:type instead of predicate to find ResourceFormat
    params.append('type', type);
  }

  const url = `${API.UI.EDIT_RESOURCE}&${params.toString()}`;

  // 3. FETCH AND ADD JSON TO STATE
  return dispatch(addResourceToModal(url, index, parentUri))
    .catch(() => {
      if (!state.modal.showing) {
        dispatch(closeModal());
      }
    });
};

/**
 * Open modal for creating a new resource
 * @param {*} predicate 
 * @param {*} parentUri object
 * @param {*} onSave execute after save & close
 * @param {*} view used to resolve
 */
export const createResource = (settings) => (dispatch, getState) => {
  const state = getState();
  // Mode is used to filter fields by modeConstraint interface.rdfs
  const mode = getViewMode(state);
  const parentUri = getParentUri(state);
  const archivedObjectUri = state.object.uri;
  const view =  state.object.view ? state.object.view : state.view.view.props.view;
  const { type, predicate } = settings;
  const resourceFormatChain = getResourceFormatChain(state.modal.resources, predicate);

  // ON SAVE & CLOSE default save method
  if (!settings.onSave) {
    settings.onSave = resource => saveResource(resource, archivedObjectUri, view, resourceFormatChain)
      .then(({ value }) => dispatch(addValue(predicate, value)));
  }
  // 1. OPEN MODAL IF NOT OPEN
  if (!state.modal.showing) {
    dispatch(openModal(settings));
  }
  // 2. URL FOR FETCHING RESOURCE JSON
  const params = new URLSearchParams({
    predicate,
    parentUri,
    mode,
    archivedObjectUri,
    view,
    resourceFormatChain
  });

  if (type) { // rdf:type instead of predicate to find ResourceFormat
    params.append('type', type);
  }

  const url = `${API.UI.CREATE_RESOURCE}&${params.toString()}`;

  // 3. FETCH AND ADD JSON TO STATE
  return dispatch(addResourceToModal(url, 0, parentUri))
    .catch(() => {
      if (!state.modal.showing) {
        dispatch(closeModal());
      }
    });
};

/**
 * Save resource (editing in modal)
 * @param {*} resource modalReducer resource
 * @returns display value of created/edited resource
 */
export const saveResource = (resource, archivedObjectUri, view, resourceFormatChain) => {
  const formData = getFormData(resource);
  const headers = {
    method: 'POST',
    body: formData
  };
  const predicate = resource.predicate;

  // ResourceFormat.folder -> create ArchivedObject
  if (resource.folder) {
    return saveResourceAsArchivedObject(resource, formData, headers, archivedObjectUri, view, resourceFormatChain);
  }
  // Editing Resource
  if (resource.uri) {
    formData.set('objectUri', resource.uri);
  }

  formData.set('predicateUri', resource.predicate);
  // May be missing if previous not created yet.
  if (resource.parentUri) {
    formData.set('parentUri', resource.parentUri);
  }
  formData.set('archivedObjectUri', archivedObjectUri);
  formData.set('classUri', resource.classUri);

  return getDisplayOnSave(fetch(API.DAM.EDIT_RESOURCE, headers), predicate, archivedObjectUri, view, resourceFormatChain);
};

export const saveResourceAsArchivedObject = (resource, formData, headers, archivedObjectUri, view, resourceFormatChain) => {
  // No uri = create new
  if (!resource.uri) {
    formData.set('parentUri', resource.folder || resource.parentUri);
    if(resource.view){
      formData.set('view', resource.view);
      formData.set('isResource', true);
    }
    if(resource.template != undefined){
      formData.set('templateItem', resource.template);
    }
    // DAM-1602 do not link attachments to this object
    formData.set('noAttachmentLink', true);
    return getDisplayOnSave(fetch(API.DAM.CREATE_OBJECT, headers), resource.predicate, archivedObjectUri, view, resourceFormatChain);
  }
  // edit existing
  else {
    formData.set('uri', resource.uri);
    return getDisplayOnSave(fetch(API.DAM.EDIT_OBJECT, headers), resource.predicate, archivedObjectUri, view, resourceFormatChain);
  }
};

// Get Display value if successful, handle errors
export const getDisplayOnSave = (promise, predicate, archivedObjectUri, view, resourceFormatChain) => {
  return promise
    .then(resToJsonHandleErrors)
    .then(json => {
      let { uri, error, isCreated } = json;
      if (error) {
        throw new Error('saveFailed');
      }
      return new Promise((resolve, reject) => {
        getResourceDisplayValue(uri, predicate, '', archivedObjectUri, view, resourceFormatChain)
          .then(value => resolve({ value, isCreated }));
      });
    });
};

// Get Value<DisplayResourceFormat>
export const getResourceDisplayValue = (uris, predicate, type, archivedObjectUri, view, resourceFormatChain) => {
  const params = new URLSearchParams({ uris, predicate, value: true });
  if (type) {
    params.append('type', type);
  }
  if (archivedObjectUri) {
    params.append('archivedObjectUri', archivedObjectUri);
  }
  if (view) {
    params.append('view', view);
  }
  if (view) {
    params.append('resourceFormatChain', resourceFormatChain);
  }
  return fetch(`${API.UI.DISPLAY_RESOURCE_VALUE}&${params.toString()}`)
    .then(resToJsonHandleErrors);
};

// Components such as Autosuggest 
// may want to fetch full DisplayResourceFormat on select
export const addResourceValue = (value, predicate) => (dispatch, getState) => {
  const state = getState();
  const archivedObjectUri = state.object.uri;
  const view =  state.object.view ? state.object.view : state.view.view.props.view;
  const resourceFormatChain = getResourceFormatChain(state.modal.resources, predicate);
  return getResourceDisplayValue(value.uri, predicate, '', archivedObjectUri, view, resourceFormatChain)
    .then(value => dispatch(addValue(predicate, value)));
};

// Send request to remove links in metadata -> only on success
// remove value from state
export const removeObjectLink = ({ value, predicate, index }) => (dispatch, getState) => {
  const params = new URLSearchParams({
    objectUri: value.uri,
    predicateUri: predicate,
    action: 'delete'
  });

  const parentUri = getParentUri(getState());
  if (parentUri) {
    params.set('parentUri', parentUri);
  }

  return fetch(`${API.DAM.LINK_OBJECT}?${params.toString()}`)
    .then(res => {
      if (res.status !== 200) {
        actionError({ message: 'deleteLinkFailed' });
      }
      dispatch(removeValue(predicate, index));
    });
};

/**
 * Connect to SuggestionsController
 * @param {*} source Dictionary
 * @param {*} input String from input field
 */
export const getSuggestions = (source, input) => {
  return fetch(`${API.DAM.SUGGESTIONS}?source=${source}&input=${input}`)
    .then(resToJsonHandleErrors)
    .then(json => json.sort((a,b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase())))
    .catch(e => console.log(e));
};

// New Resources created with modal need a metadata template
// for setting parentUri for nested resources.
export const getTemplate = () => (dispatch) => {
  return fetch(API.UI.GET_TEMPLATE)
    .then(resToJsonHandleErrors)
    .then(json => {
      const { template, error } = json;
      if (template) {
        dispatch({type: SET_TEMPLATE, template});
      } else if (error) {
        console.log(error);
      }
    });
};

/**
 * @param {*} resources previous modal format uris
 * @param {*} predicate current modal format uri
 * @returns combines all modal uris to a chain.
 */
export const getResourceFormatChain = (resources, predicate) => {
  let formatChain = '';
  if (resources && resources.length > 0) {
    formatChain = resources
      .map(res => res.classUri)
      .reduce((a, b) => a + FORMAT_DELIMETER + b);
  }
  if (predicate) {
    formatChain = formatChain + FORMAT_DELIMETER + predicate;
  }
  return formatChain;
};